import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { calculateSeatPricing } from '../../../selectors/reserved-seating';
import { SeatsioSeatingChart } from '@seatsio/seatsio-react';
import { getJSFormattedCurrency } from '@h/currency';
import WithCurrencyConfig from '../../../config/WithCurrencyConfig';

class SeatsioChart extends Component {

    state = {
        chartObject: null,
        resToken: null,
        seatPricing: null,
        requestedBestAvailable: false,
        pricesWithoutFeeMap: {},
    }

    /**
     * Any prop change to the seatsio chart component causes a rerender.
     * We would like to avoid that, so instead of letting prop changes propagate,
     * pick which changes should affect the chart and send them using changeConfig
     */
    shouldComponentUpdate(nextProps, nextState) {
        // reservation token
        if (nextProps.reservationToken != this.state.resToken) {
            this.setState({resToken: nextProps.reservationToken});
            if (this.state.chartObject) {
                this.state.chartObject.changeConfig({
                    extraConfig: {
                        ...this.state.chartObject.extraConfig,
                        res_token: nextProps.reservationToken,
                    }
                })
            }
        }

        // seat pricing
        const seatPricing = calculateSeatPricing(nextProps.priceLevels, nextProps.sectionPriceLevels, nextProps.sectionFeeMap, this.includesFee());

        if (JSON.stringify(seatPricing) != JSON.stringify(this.state.seatPricing)) {
            this.setState({seatPricing, pricesWithoutFeeMap: this.getPricesWithoutFeeMap(seatPricing)});
            if (this.state.chartObject) {
                this.state.chartObject.changeConfig({
                    pricing: seatPricing,
                });
            }
        }

        // front end best available
        if (seatPricing && this.state.chartObject && !!this.props.selectBestAvailable && !this.state.requestedBestAvailable) {
            this.state.chartObject.selectBestAvailable(this.props.selectBestAvailable);
            this.setState({requestedBestAvailable: true});
        }

        return false;
    }

    formatCurrency = amount => {
        return this.props.currencyConfig ? getJSFormattedCurrency(+amount, this.props.currencyConfig) : '';
    }

    getUnavailableCategories = () => {
        return Object.values(this.props.priceLevels).filter(lvl => +lvl?.transaction_limit < 1).map(lvl => lvl.name);
    }

    includesFee = () => {
      return this.props.discloseFee;
    }

    getTooltipInfo = object => {
        const baseSeatInfo = this.includesFee() ? (this.props.feeVerbiage ? '[i]Includes Ticket + Fee[/i]': '[i]Includes Ticket + Service Fee[/i]') : '';

        if (object.objectType && object.objectType == 'section') {
            const priceGreaterThan0 = object.selectableCategories.some(element => {
                if (element.pricing.price !== undefined) {
                    return element.pricing.price > 0;
                } else if (element.pricing.ticketTypes && Array.isArray(element.pricing.ticketTypes)) {
                    return element.pricing.ticketTypes.some(ticketType => ticketType.price > 0);
                }
                return false;
            });
          return priceGreaterThan0 ? baseSeatInfo : '';
        }

        const seat_status = object.status ? object.status.split(":") : [null];
        if (seat_status[0] !== 'free') {
            return baseSeatInfo;
        }
        if (Array.isArray(object?.pricing?.ticketTypes)) {
            return baseSeatInfo + (baseSeatInfo ? '[br/]' : '') + object.pricing.ticketTypes.map(ticketType => {
              const priceWithoutFee = this.state.pricesWithoutFeeMap[ticketType.price] ?? 0;
              const ticketFaceValue = priceWithoutFee ? this.formatCurrency(priceWithoutFee) : ticketType.formattedPrice;
              let priceString = `[b]${ticketType.ticketType}[/b]: ${ticketFaceValue}`;
              const cost = (priceWithoutFee) ? priceWithoutFee : ticketType.price;
              if (this.props.sectionFeeMap[cost] !== undefined) {
                priceString += ` + ${this.formatCurrency(this.props.sectionFeeMap[cost])}`;
                priceString += (this.props.feeVerbiage ? ` Fee` : ` Service Fee`);
              }
              return priceString;
            }).join("[br/]")
        }
        return (object?.pricing?.price > 0 ) ? baseSeatInfo : '';
    }

    getSeatColor = (seat, defaultColor, extraConfig) => {
        const seat_status = seat.status.split(":");
        if (seat_status.length > 1 && seat_status[0] === 'in_res' && seat_status[1] == extraConfig.res_token) {
            return "#FFDE17";
        }
    }

    onRenderStarted = chart => {
        this.setState({chartObject: chart});

        const iframe = document.querySelector('.seating-chart__chart-container iframe');

        if (iframe) {
            iframe.setAttribute('tabindex', '-1');
        }
    }

    getMaxSelectedObjects = () => {
        let maxSelectedObjects = null;
        if (this.props.selectBestAvailable) {
            maxSelectedObjects = 0;
            if (!!this.props.selectBestAvailable.number) {
                maxSelectedObjects += +this.props.selectBestAvailable.number;
            }
            if (typeof this.props.selectBestAvailable?.ticketTypes === 'object' && this.props.selectBestAvailable?.ticketTypes !== null) {
                Object.values(this.props.selectBestAvailable.ticketTypes).forEach(quantity => {
                    maxSelectedObjects += +quantity;
                });
            }
        }
        return maxSelectedObjects;
    }

    getPricesWithoutFeeMap = (seatPricing) => {
      let pricesWithoutFeeMap = {};
      seatPricing.map(category => {
        if (category.ticketTypes) {
          category.ticketTypes.map(t => {
            pricesWithoutFeeMap[t.price] = t.priceWithoutFee;
          })
        }
      });
      return pricesWithoutFeeMap;
    }

    render() {
        let selectionValidators = [];
        if (!this.props.allowOrphanSeats) {
            selectionValidators.push({type:'noOrphanSeats'});
        }
        if (this.props.forceConsecutiveSeats) {
          selectionValidators.push({type:'consecutiveSeats'});
        }

        const seatsioOptions = {
            workspaceKey: this.props.publicKey,
            event: this.props.seatsioEventId,
            region: "eu", // should this be dynamic?
            pricing: this.state.seatPricing,
            hidePoweredBy: true,
            multiSelectEnabled: false,
            // leave it here just in case
            // since user get to seat picker in any way just disable this property at all
            // static: this.props.onlyShowBestAvailable, // disable user interaction for best_available_only events
            priceFormatter: this.formatCurrency,
            onObjectSelected: this.props.onObjectSelected,
            onObjectClicked: this.props.onObjectClicked,
            onObjectDeselected: this.props.onObjectDeselected,
            onSelectionValid: this.props.onSelectionValid,
            priceLevelsTooltipMessage: this.props.priceLevelSelectString,
            tooltipInfo: this.getTooltipInfo,
            objectColor: this.getSeatColor,
            useObjectUuidsInsteadOfLabels: true,
            extraConfig: {
                res_token: this.state.resToken,
                seatsio_public_key: this.props.publicKey,
                // leave it here just in case
                // since user get to seat picker in any way just disable this property at all
                // best_available_only: this.props.onlyShowBestAvailable,
                priceLevelSelect: this.props.priceLevelSelectString,
                locale: this.props.locale,
            },
            selectionValidators: selectionValidators,
            messages: {
                "noOrphanSeats" : this.props.orphanSeatMessage,
            },
            unavailableCategories: this.getUnavailableCategories(),
            onRenderStarted: this.onRenderStarted,
            maxSelectedObjects: this.getMaxSelectedObjects(),
            onChartRendered: this.props.onChartRendered,
            onSelectionInvalid: this.props.onSelectionInvalid,
        };

        // front end best available selection
        if (!!this.props.selectBestAvailable) {
            seatsioOptions.onBestAvailableSelected = this.props.onBestAvailableSelected;
            seatsioOptions.onBestAvailableSelectionFailed = this.props.onBestAvailableSelectionFailed;
        }
        return <SeatsioSeatingChart {...seatsioOptions} />
    }

}

SeatsioChart.propTypes = {
    onBestAvailableSelectionFailed: PropTypes.func,
    onBestAvailableSelected: PropTypes.func,
    onObjectSelected: PropTypes.func.isRequired,
    onObjectDeselected: PropTypes.func.isRequired,
    onObjectClicked: PropTypes.func.isRequired,
    onSelectionValid: PropTypes.func.isRequired,
    priceLevels: PropTypes.object.isRequired,
    sectionPriceLevels: PropTypes.array.isRequired,
    publicKey: PropTypes.string.isRequired,
    seatsioEventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onlyShowBestAvailable: PropTypes.bool,
    hasTicketsInReservation: PropTypes.bool,
    priceLevelSelectString: PropTypes.string,
    reservationToken: PropTypes.string,
    locale: PropTypes.string.isRequired,
    allowOrphanSeats: PropTypes.bool,
    orphanSeatMessage: PropTypes.string,
    discloseFee: PropTypes.bool,
    sectionFeeMap: PropTypes.object,
    feeVerbiage: PropTypes.bool,
    forceConsecutiveSeats: PropTypes.bool,
    forceConsecutiveSeatsMessage: PropTypes.string,
}

SeatsioChart.defaultProps = {
    priceLevelSelectString: 'Select a Ticket Type',
    onlyShowBestAvailable: false,
    hasTicketsInReservation: false,
    allowOrphanSeats: false,
    discloseFee: true,
    sectionFeeMap: {},
    feeVerbiage: false,
    forceConsecutiveSeats: false,
    forceConsecutiveSeatsMessage: '',
}

export default WithCurrencyConfig(SeatsioChart);
